import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import Callout from "components/callout/callout"
import DeletedDocCallout from "@/components/callout/deletedDocCallout"
import { useTranslation } from "react-i18next"
import { textToHTML } from "utils/converters"
import useCategories from "hooks/useCategories"
import ProductsList from "@/features/storages/products/productList"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import SectionDivider from "components/layout/sectionDivider"
import RightSidebarCategory from "components/sidebar/right/RightSidebarCategory"
import Button from "components/inputs/button"
import CategoryTags from "components/tags/categoryTags"
import SectionHeaderActions from "components/layout/sectionHeaderActions"
import PageTitle from "features/navigation/pageTitle"
import useAccess from "@/hooks/useAccess"
import { AccessRole, FormStatus, DeviceSize } from "@/types"
import useStorages from "@/hooks/useStorages"
import { paramGuardCategory, paramGuardStorage } from "@/utils/paramGuard"
import FloatingButtonGroup from "@/components/buttons/floatingButtonGroup"
import FloatingButtonGroupButton from "@/components/buttons/FloatingButtonGroupButton"
import FloatingButtonGroupHr from "@/components/buttons/FloatingButtonGroupHr"
import useLayout from "@/hooks/useLayout"
import IconButton from "@/components/buttons/iconButton"
import { ReactComponent as MoreVertIcon } from "assets/icons/more_vert.svg"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import { useState } from "react"
import TableList from "@/components/list/tableList/tableList"
import { MainSheetHeaderActions } from "@/components/layout/layoutChrome"
import TableListRow from "@/components/list/tableList/tableListRow"
import TableListCell from "@/components/list/tableList/tableListCell"

const Category = () => {

    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const state = location.state as { new: boolean }
    const { displayIfSize } = useWindowDimensions()

    const { targetStorageDocId: storageId } = useTargetStorage()
    const { categoryId } = useParams<{ categoryId: string }>()
    
    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    const {
        category,
        categoryStatus,
        categoriesInitialized,
        allCategories,
        restoreCategoryAsync,
        isRestoring
    } = useCategories({ storageId, categoryId })

    const {
        storage,
        storageStatus,
    } = useStorages({ storageId })

    const { openDialog, addToast } = useLayout()
    const [isRestoringState, setIsRestoringState] = useState(false)

    const isLoading = storageStatus === FormStatus.LOADING || !category?.name;

    const getCategoryPath = (id: string): string[] => {
        const path: string[] = []
        let current = allCategories.find(c => c && c.id === id)
        while (current && current.relations?.parentCategory) {
            path.unshift(current.relations.parentCategory)
            current = allCategories.find(c => c && c.id === current?.relations?.parentCategory)
        }
        return path
    }

    const getChildCategoriesIdArray = (id: string): string[] => {
        return allCategories.filter(c => c && c.relations?.parentCategory === id).map(c => c.id!).filter(Boolean)
    }

    const categoryIdArrayToNameArray = (ids: string[]): string[] => {
        return ids.map(id => {
            const c = allCategories.find(c => c && c.id === id)
            return c ? `${c.categoryId}: ${c.name}` : ''
        }).filter(Boolean)
    }

    // Check params and declare safe ones to use in JSX
    if (
        !paramGuardStorage({storageId, storage, storageStatus, navigate}) ||
        !paramGuardCategory({storageId, categoryId, category, categoryStatus, navigate})
    ) {
        return <></>
    }
    const safeStorageId = storageId!;
    const safeCategoryId = categoryId!;

    const handleRestoreClick = async () => {
        setIsRestoringState(true)
        try {
            await restoreCategoryAsync({
                storageId: safeStorageId,
                categoryId: safeCategoryId
            })
            addToast({
                text: t('categoryRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    // Return the JSX.
    return (
        <>
            <PageTitle title={category?.name ? `${t('titleCategory')}: ${category.name}` : t('titleCategory')} />
            <MainSheetHeaderActions>
                {displayIfSize([DeviceSize.SM]) && categoriesInitialized && !category?.isDeleted && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR]) &&
                    <FloatingButtonGroup id={`storage_edit_${storageId}`} openingButton={<IconButton icon={<MoreVertIcon />} />}>
                        <FloatingButtonGroupButton>
                            <Link to={`/storage/categories/${categoryId}/edit`}>{t('editCategory')}</Link>
                        </FloatingButtonGroupButton>
                        <FloatingButtonGroupButton>
                            <Link to={`/storage/categories/${categoryId}/edit_custom_inputs`}>{t('editInputs')}</Link>
                        </FloatingButtonGroupButton>
                        <FloatingButtonGroupHr />
                        <FloatingButtonGroupButton>
                            <Link
                                to={`/storage/categories/create`}
                                state={{
                                    categoryId: categoryId,
                                }}
                            >{t('buttonNewChildCategory')}</Link>
                        </FloatingButtonGroupButton>
                        <FloatingButtonGroupHr />
                        <FloatingButtonGroupButton onClick={() => openDialog(`delete_category`, {storageId, categoryId})}>
                            {t('delete')}
                        </FloatingButtonGroupButton>
                    </FloatingButtonGroup>
                }
            </MainSheetHeaderActions>
            {category?.isDeleted && (
                <DeletedDocCallout
                    documentType="category"
                    deletionType="hard"
                    action={handleRestoreClick}
                    isRestoring={isRestoring || isRestoringState}
                    deletedAt={category?.keyHistory?.deletedAt}
                />
            )}
            <Section>
                {/* Print out parent category path if any */}
                {category && category.relations?.parentCategory && <CategoryTags
                    label={t(`labelCategoryPath`)}
                    categoryNames={categoryIdArrayToNameArray(getCategoryPath(safeCategoryId))}
                    categoryIds={getCategoryPath(safeCategoryId)}
                    storageId={safeStorageId}
                />}
                {/* Print out child categories if any */}
                {category && getChildCategoriesIdArray(safeCategoryId).length > 0 &&
                    <CategoryTags
                        label={t(`labelChildCategories`)}
                        categoryNames={categoryIdArrayToNameArray(getChildCategoriesIdArray(safeCategoryId))}
                        categoryIds={getChildCategoriesIdArray(safeCategoryId)}
                        separator=''
                        storageId={safeStorageId}
                    />
                }

                <TableList width="fit">
                    {!isLoading ?
                        <>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelDescription')}</b></TableListCell>
                                <TableListCell>{category?.description || '-'}</TableListCell>
                            </TableListRow>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelStorageRequirements')}</b></TableListCell>
                                <TableListCell>{category?.storageRequirements || '-'}</TableListCell>
                            </TableListRow>
                            <TableListRow hoverEffect={false}>
                                <TableListCell isLabel={true}><b>{t('labelNotes')}</b></TableListCell>
                                <TableListCell>{category?.notes || '-'}</TableListCell>
                            </TableListRow>
                        </>
                        :
                        <>
                            {Array.from({ length: 3 }).map((_, _index) => (
                                <TableListRow key={_index} hoverEffect={false}>
                                    <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                    <TableListCell><p className="skeleton short"></p></TableListCell>
                                </TableListRow>
                            ))}
                            <TableListRow hoverEffect={false}>
                                <TableListCell><p className="skeleton shortest"></p></TableListCell>
                                <TableListCell>
                                    <div className="grid gap-y-2">
                                        <p className="skeleton long"></p>
                                        <p className="skeleton medium"></p>
                                        <p className="skeleton short"></p>
                                    </div>
                                </TableListCell>
                            </TableListRow>
                        </>
                    }
                </TableList>
            </Section>
            {state?.new && <Callout type={`success`} title={t(`calloutCategoryCreatedTitle`)} id={`callout_category_created_${categoryId}`}>
                <p>{t(`calloutCategoryCreatedText`)}</p>
                <Link
                    className="block w-full md:inline-block md:w-fit"
                    to={`/storage/categories/${categoryId}/edit_custom_inputs`}
                    state={{
                        new: true,
                    }}
                ><Button wideOnMobile design="primary"><AddIcon />{t(`buttonAddCustomInputs`)}</Button></Link>
            </Callout>}
            
            <SectionDivider />
            
            <Section padX={false}>
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h2>{t(`titleProducts`)}</h2>
                    </SectionHeaderTitle>
                    <SectionHeaderActions>
                        { limitToRoles([AccessRole.OWNER, AccessRole.EDITOR]) && !category?.isDeleted &&
                            <>
                                {!displayIfSize([DeviceSize.SM]) && <Button wideOnMobile
                                    to={`/storage/products/create`}
                                    state={{
                                        categoryId: categoryId,
                                    }}
                                    design={`primary`}>
                                    <AddIcon />
                                    <span>{t('buttonNewProduct')}</span>
                                </Button>}
                                {displayIfSize([DeviceSize.SM]) && <IconButton
                                    icon={<AddIcon />}
                                    colorScheme="blue"
                                    to={`/storage/products/create`}
                                    state={{
                                        categoryId: categoryId,
                                    }}
                                />}
                            </>
                        }
                    </SectionHeaderActions>
                </SectionHeader>
                <ProductsList storageId={safeStorageId} categoryId={safeCategoryId} displayHelper={!category?.isDeleted} enableActions={!category?.isDeleted} />
            </Section>
            <RightSidebarCategory categoryId={safeCategoryId} storageId={safeStorageId} />
        </>
    )
}

export default Category

